import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import axios from 'axios';
import { withStyles } from '@material-ui/core/styles';
import Select from 'components/forms/Select';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Checkbox from 'components/forms/Checkbox';
import NotificationSystem from 'rc-notification';
import DownloadIcon from 'mdi-react/DownloadIcon';
import BinocularIcon from 'icons/binocular';
import Button from 'components/buttons/Button';
import { get, first, noop, isEmpty } from 'lodash';
import { getLastMsg } from 'utils/inbox';
import { Can, AbilityContext } from 'utils/permissions';
import DateRangePicker from 'components/forms/DateRangePicker';

import { redirectApp } from 'utils/misc';
import BasicNotification from 'components/Notification';
import { PAGE_SIZE_LARGE } from 'constants/config';
import { REPLY_STATUS_OPTIONS, MESSAGE_LABEL_OPTIONS } from 'constants/outreach';
import {
  getMessage,
  getMessages,
  patchMessage,
  setMessage,
  destroyBulkMessage,
  destroyMessage,
  setMessageAsReadBulk,
  toggleInboxDetail,
  resetMessage,
  resetMessages,
  setAutoCategorizedDateMessage,
  setMessageArchivedBulk,
} from 'redux/actions/messageActions';
import { getOpportunities } from 'redux/actions/opportunityActions';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import {
  NOTIFICATION_NODE_STYLE,
  MIN_DESKTOP_WIDTH,
} from 'constants/misc';
import { apiInboxExportCSV } from 'constants/apiRouting';
import Pagination from 'components/pagination/Pagination';
import {
  getOutreach,
  updateOutreachThreadStatusBulk,
  setOutreach,
} from 'redux/actions/outreachActions';
import { CHANGE_INBOX_PAGE, DOWNLOAD_INBOX_CSV } from 'constants/analytics';
import {
  DynamicFilter,
  AccountsFilter,
  OpportunityFilter,
  CampaignsFilter,
  CampaignManagersFilter,
} from 'components/filters';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import OutreachRow from './OutreachRow';
import MessageActionIcons, { ACTION_MARK_AS_READ, ACTION_DELETE, ACTION_ARCHIVE } from './MessageActionIcons';
import { analyticsTrack } from '../../../utils/analytics';
import { getFiltersFromLocation, setFiltersToLocation } from '../../../utils/misc';
import OpportunityView from '../../Opportunities/OpportunityView';


const FilterTypes = {
  category: 'category',
  manager: 'manager',
  date: 'date',
  status: 'status',
  opportunities: 'opportunities',
  account: 'account',
  campaign: 'campaign',
};


const styles = theme => ({
  optionsWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '-1rem',
  },
  noResultsTable: {
    height: 'calc(100vh - 14rem)',
  },
  slideContainer: {
    marginTop: '1rem',
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.white,
    position: 'relative',
    width: '100%',
    overflowX: 'hidden',
    boxShadow: theme.shadows[0],
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      position: 'absolute',
      width: '96vw',
      left: '-0.75rem',
    },
  },
  loaderList: {
    height: '100vh',
    position: 'relative',
  },
  outreachTable: {
    borderBottom: `1px solid ${theme.palette.secondary['200']}`,
    overflow: 'hidden',
  },
  outreachesContainer: {
    height: 'calc(100vh - 16.3rem)',
    overflow: 'auto',
  },
  campaignDetailInbox: {
    height: 'calc(100vh - 19.8rem)',
  },
  noRepliesStateContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noRepliesIcon: {
    color: theme.palette.secondary['300'],
    width: '5.75rem',
    height: '5.75rem',
  },
  noRepliesText: {
    fontSize: '1rem',
    color: theme.palette.secondary.text,
    marginTop: '1.5rem',
    fontWeight: 600,
  },
  filterWrapper: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap',
  },
  selectActions: {
    width: '10.625rem',
  },
  checkBoxAll: {
    padding: '0.375rem',
  },
});

class OutreachList extends Component {
  static propTypes = {
    setMessage: PropTypes.func.isRequired,
    getMessage: PropTypes.func.isRequired,
    getMessages: PropTypes.func.isRequired,
    patchMessage: PropTypes.func.isRequired,
    setMessageAsReadBulk: PropTypes.func.isRequired,
    setMessageArchivedBulk: PropTypes.func.isRequired,
    destroyBulkMessage: PropTypes.func.isRequired,
    destroyMessage: PropTypes.func.isRequired,
    updateOutreachThreadStatusBulk: PropTypes.func.isRequired,
    setAutoCategorizedDateMessage: PropTypes.func.isRequired,
    campaignId: PropTypes.number,
    messageId: PropTypes.string,
    user: PropTypes.shape({
      pk: PropTypes.number,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      isAuthenticated: PropTypes.bool,
      user_groups: PropTypes.array,
    }).isRequired,
    campaign: PropTypes.shape({
      id: PropTypes.number,
      account: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    campaigns: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    messages: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    accounts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
      })),
    }).isRequired,
    users: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    classes: PropTypes.element.isRequired,
    toggleInboxDetail: PropTypes.func.isRequired,
    resetMessage: PropTypes.func.isRequired,
    getOpportunities: PropTypes.func.isRequired,
    message: PropTypes.shape({
      isOpen: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
    }).isRequired,
    filterStartDate: PropTypes.func,
    filterEndDate: PropTypes.func,
    includeUnknownCampaignOption: PropTypes.bool,
    campaignRedirect: PropTypes.bool,
    location: PropTypes.shape({
      state: PropTypes.shape({
        data: PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.number,
        }),
      }),
      path: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      search: PropTypes.string,
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }),
  };

  static defaultProps = {
    campaignId: null,
    filterStartDate: null,
    filterEndDate: null,
    includeUnknownCampaignOption: true,
    campaignRedirect: false,
    messageId: null,
    location: null,
    history: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      filterParams: {
        statuses: [],
        account__id: [],
        opportunity__id: !props.campaignId ? [{ label: 'All', value: 'all' }] : [],
        opportunity__isnull: props.campaignId ? undefined : false,
        campaign_manager__id: [],
        campaign__id: [],
        redirect: get(this.props, 'location.state.data', false),
        startDate: !props.messageId && props.filterStartDate,
        endDate: !props.messageId && props.filterEndDate,
      },
      isBulkModalOpen: false,
      selectedIds: new Set(),
      lastFocusedRow: null,
      isSliding: false, // controls blink on OutreachRow
      disableCSVDownload: props.messageId,
      bulkOutreachLoading: false,
      ongoingRequestCancel: noop,
      openDeleteModel: false,
      openOpportunityViewID: null,
      selectedOutreach: null,
    };
    this.toggleBulkModal = this.toggleBulkModal.bind(this);
  }
  toggleOpportunityView = (id = null) => {
    this.setState({ openOpportunityViewID: this.state.openOpportunityViewID ? null : id });
  };
  async componentDidMount() {
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({}, n => this.notification = n);
    const { campaignId, messageId, campaignRedirect } = this.props;

    let defaultParams = {};
    if (this.isFilterParamsInQuery()) {
      const emptyParams = {
        statuses: [],
        account__id: [],
        opportunity__id: [],
        opportunity__isnull: true,
        campaign_manager__id: [],
        campaign__id: [],
        startDate: null,
        endDate: null,
      };
      defaultParams = { ...emptyParams, ...this.getFiltersFromQweryParams() };
      if (campaignId) {
        delete defaultParams.opportunity__isnull;
      }
    } else {
      defaultParams = {
        statuses: [],
        campaign__id: campaignId ? [{ value: campaignId }] : [],
        labels: [{ label: 'Unarchived', value: 'unarchived' }],
        opportunity__id: !campaignId ? [{ label: 'All', value: 'all' }] : [],
        page: 1,
      };
    }
    if (campaignRedirect) {
      defaultParams = {
        statuses: [],
        campaign__id: campaignId ? [{ value: campaignId }] : [],
        page: 1,
      };
    }
    if (get(this.props, 'location.state.data', false)) {
      defaultParams = {
        opportunity_ids: this.props.location.state.data.value,
        opportunity__id: this.props.location.state.data,
        redirect: true,
        page: 1,
      };
    }
    if (!messageId) {
      this.fetchList({ ...defaultParams });
    } else {
      await this.props.getMessages({ message_ids: [messageId] });
      await this.props.setMessage(first(this.props.messages.results));
      this.props.getMessage(messageId);
      this.props.toggleInboxDetail();
    }
  }

  isFilterParamsInQuery() {
    return get(this.props, 'location.search', false);
  }

  getFiltersFromQweryParams() {
    let qweryParams = {};
    if (!this.isFilterParamsInQuery()) return qweryParams;
    const filtersData = getFiltersFromLocation(this.props.location);
    const { startDate, endDate, ...restFilters } = filtersData;
    if (startDate) restFilters.startDate = moment(new Date(startDate));
    if (endDate) restFilters.endDate = moment(new Date(endDate));
    qweryParams = { opportunity__isnull: isEmpty(filtersData?.opportunity__id), ...restFilters };
    return qweryParams;
  }

  checkURLHaveFilterByAlias(name) {
    let rezult = false;
    if (!this.isFilterParamsInQuery()) return rezult;
    const filtersData = getFiltersFromLocation(this.props.location);
    switch (name) {
      case FilterTypes.manager:
        rezult = !isEmpty(filtersData?.campaign_manager__id);
        break;
      case FilterTypes.category:
        rezult = !isEmpty(filtersData?.statuses);
        break;
      case FilterTypes.campaign:
        rezult = !isEmpty(filtersData?.campaign__id);
        break;
      case FilterTypes.account:
        rezult = !isEmpty(filtersData?.account__id);
        break;
      case FilterTypes.date:
        rezult = Boolean(filtersData?.startDate || filtersData?.endDate);
        break;
      case FilterTypes.status:
        rezult = Boolean(filtersData?.labels);
        break;
      case FilterTypes.opportunities:
        rezult = !isEmpty(filtersData?.opportunity__id);
        break;
      default:
        break;
    }
    return rezult;
  }

  componentDidUpdate(prevProps) {
    const { campaignId, message: { isOpen: detailOpen, id: messageId } } = this.props;
    const prevDetailOpen = prevProps.message.isOpen;

    const campaignUrlPart = campaignId ? `/campaigns/${campaignId}` : '';
    if (!detailOpen && prevDetailOpen) {
      window.history.replaceState(null, null, `${campaignUrlPart}/inbox`);
    } else if ((detailOpen && !prevDetailOpen) || (prevProps.message.id !== messageId && detailOpen)) {
      window.history.replaceState(null, null, `${campaignUrlPart}/inbox/${this.props.message.id}`);
    }
  }

  componentWillUnmount() {
    this.notification.destroy();
    if (this.props.message.isOpen) {
      this.props.toggleInboxDetail();
    }
    this.props.resetMessage();
  }

  onResetSelectedFilter = (filter) => {
    const newParams = {};
    switch (filter) {
      case FilterTypes.category:
        newParams.statuses = [];
        break;
      case FilterTypes.campaign:
        newParams.campaign__id = [];
        break;
      case FilterTypes.manager:
        newParams.campaign_manager__id = [];
        break;
      case FilterTypes.account:
        newParams.account__id = [];
        break;
      case FilterTypes.date:
        newParams.startDate = null;
        newParams.endDate = null;
        break;
      case FilterTypes.status:
        newParams.labels = null;
        break;
      case FilterTypes.opportunities:
        newParams.opportunity__id = [];
        break;
      default:
        break;
    }
    this.setState({ filterParams: { ...this.state.filterParams, ...newParams } });
  }

  onResetFilters = () => {
    const { campaignId } = this.props;
    const newState = this.state;
    newState.filterParams.statuses = [];
    newState.filterParams.account__id = [];
    newState.filterParams.opportunity__id = [];
    newState.filterParams.campaign_manager__id = [];
    newState.filterParams.campaign__id = campaignId ? [{ value: campaignId }] : [];
    newState.filterParams.startDate = null;
    newState.filterParams.endDate = null;
    newState.filterParams.labels = null;
    newState.disableCSVDownload = false;
    delete newState.filterParams.opportunity__isnull;
    this.setState(newState, () => this.fetchList(newState.filterParams));
  }

  onDeleteFilter = (selectedFilter) => {
    const newState = this.state;

    switch (selectedFilter) {
      case FilterTypes.category:
        newState.filterParams.statuses = [];
        break;
      case FilterTypes.campaign:
        newState.filterParams.campaign__id = [];
        break;
      case FilterTypes.manager:
        newState.filterParams.campaign_manager__id = [];
        break;
      case FilterTypes.account:
        newState.filterParams.account__id = [];
        break;
      case FilterTypes.date:
        newState.filterParams.startDate = null;
        newState.filterParams.endDate = null;
        break;
      case FilterTypes.status:
        newState.filterParams.labels = null;
        break;
      case FilterTypes.opportunities:
        newState.filterParams.opportunity__id = [];
        delete newState.filterParams.opportunity__isnull;
        break;
      default:
        break;
    }
    this.setState(newState, () => this.fetchList(newState.filterParams));
  }

  getSelectedOutreachesOnPage(includeEmptyOutreaches = true) {
    if (includeEmptyOutreaches) {
      return this.props.messages.results.filter(o => this.state.selectedIds.has(o.id));
    }
    return this.props.messages.results.filter(o => this.state.selectedIds.has(o.id) && o.sequence_contact);
  }

  handleChangeAccounts = (accounts) => {
    this.fetchList({ account__id: accounts, page: 1 });
  }

  handleChangeOpportunities = (opportunities) => {
    const currentSelection = opportunities[opportunities.length - 1];
    if (currentSelection.value === 'all') {
      this.fetchList({
        opportunity__isnull: false,
        opportunity__id: [{ label: 'All', value: 'all' }],
        page: 1,
      });
    } else if (currentSelection.value === 'unknown') {
      this.fetchList({
        opportunity__isnull: true,
        opportunity__id: [{ label: 'Unknown', value: 'unknown' }],
        page: 1,
      });
    } else {
      const newFilterParams = { ...this.state.filterParams };
      if (newFilterParams.opportunity__isnull === true || newFilterParams.opportunity__isnull === false) {
        delete newFilterParams.opportunity__isnull;
      }
      const filterOpp = opportunities.filter(opp => !['all', 'unknown'].includes(opp.value));
      this.setState(
        { filterParams: { ...newFilterParams } },
        () => this.fetchList({
          opportunity__id: filterOpp,
          page: 1,
        }),
      );
    }
  }

  handleChangeCampaigns = (campaigns) => {
    this.fetchList({ campaign__id: campaigns, page: 1 });
  }

  toggleDetail = async (event, messageSelected, firstMsg) => {
    if (event.target.nodeName !== 'INPUT' && // not clicked on checkbox
      event.target.nodeName !== 'SPAN' && // not clicked on campaign tag
      !event.target.className.includes('dot')) { // not clicked on read dot
      const detailOpen = this.props.message.isOpen;
      await this.props.setMessage({ ...firstMsg, loaded: false } || {});
      await this.props.getMessage(firstMsg.id);
      const accountIDs = get(this.props.message, 'email_service_provider_config_accounts', null);
      if (accountIDs) {
        this.props.getOpportunities({
          account_id: accountIDs.length > 0 && accountIDs.map(l => (l)).join(','),
        });
      }
      // If detail is open and a new message is clicked, display the new message without closing detail
      if (!detailOpen) {
        this.props.toggleInboxDetail();
      }
      if (!firstMsg.is_read) {
        this.props.patchMessage(firstMsg.id, { is_read: true });
      }
    }
  }

  handleChangeCategories = (statuses) => {
    this.fetchList({ statuses, page: 1 });
  }

  handleChangeStatus = (labels) => {
    this.fetchList({ labels, page: 1 });
  }

  handleChangeMoveTo = async (newStatus) => {
    const objectsListUpdate = this.getSelectedOutreachesOnPage(false).map(obj => ({
      id: obj.outreach,
      thread_status: {
        code: newStatus.value,
        verbose: newStatus.label,
      },
    }));
    const messagesIds = this.getSelectedOutreachesOnPage(false).map(obj => obj.id);
    const allSelectedMessages = this.getSelectedOutreachesOnPage();
    const messagesWithoutOutreaches = allSelectedMessages.length - objectsListUpdate.length;
    if (messagesWithoutOutreaches) {
      this.showNotification(`${messagesWithoutOutreaches}
       messages can not be moved because they are not associated with an outreach, please fix.`, 'danger');
      return;
    }
    this.setState({ bulkOutreachLoading: true });
    await this.props.updateOutreachThreadStatusBulk(objectsListUpdate, false);
    await messagesIds.forEach((messageId) => {
      this.props.setAutoCategorizedDateMessage({
        messageId,
        autoCategorizedDate: null,
      });
    });
    const { campaignId } = this.props;
    const defaultParams = {
      statuses: [],
      campaign__id: campaignId ? [{ value: campaignId }] : [],
      labels: [{ label: 'Unarchived', value: 'unarchived' }],
      page: 1,
    };
    this.fetchList({ ...defaultParams });
    this.setState({ bulkOutreachLoading: false });
    this.untoggleSelectedOutreachesOnPage();
  }

  handleLastpage = () => {
    const count = get(this.props.messages, 'count', 0);
    const totalPages = Math.ceil(count / PAGE_SIZE_LARGE);
    if (totalPages === this.state.filterParams.page && this.state.filterParams.page !== 1) {
      this.fetchList({ page: totalPages - 1 });
    } else { this.fetchList(); }
  };

  handleChangeActions = async (action) => {
    const selectedObjectsList = this.getSelectedOutreachesOnPage().map(obj => ({
      id: obj.id,
    }));
    switch (action.value) {
      case ACTION_MARK_AS_READ:
        await this.props.setMessageAsReadBulk(selectedObjectsList);
        this.untoggleSelectedOutreachesOnPage();
        this.fetchList();
        break;
      case ACTION_ARCHIVE:
        await this.props.setMessageArchivedBulk(selectedObjectsList);
        this.untoggleSelectedOutreachesOnPage();
        this.handleLastpage();
        break;
      case ACTION_DELETE:
        this.toggleBulkModal(true);
        break;
      default:
        break;
    }
  }

  handleDateChange = (startDate, endDate) => {
    const { filterParams } = this.state;
    const filterParamsNew = { ...filterParams, startDate, endDate };
    if (endDate && startDate) {
      // fetchList only if both startDate and endDate are present
      this.fetchList({ startDate, endDate });
    } else if (endDate || startDate) {
      // setState if startDate or endDate is null
      this.setState({ filterParams: filterParamsNew });
    } else if (!endDate && !startDate) {
      // reset search if both are null
      this.fetchList({ startDate: null, endDate: null });
    }
  }

  handleChangeManagers = (managers) => {
    this.fetchList({ campaign_manager__id: managers, page: 1 });
  }

  handleChangePage = (params) => {
    analyticsTrack(CHANGE_INBOX_PAGE, params, true);
    this.fetchList(params);
  }

  fetchList = (filterParams, seamless = false) => {
    let preparedParams = null;
    const paramsForState = { ...this.state.filterParams, ...filterParams };
    const { location, history } = this.props;
    setFiltersToLocation(location, history, paramsForState);
    if (paramsForState.redirect) {
      paramsForState.startDate = null;
      paramsForState.endDate = null;
    }
    preparedParams = this.prepareParams(paramsForState);
    this.setState({ filterParams: paramsForState, disableCSVDownload: false });
    this.state.ongoingRequestCancel();
    const cancelToken = new axios.CancelToken((c) => { this.setState({ ongoingRequestCancel: c }); });
    this.props.getMessages(preparedParams, seamless, cancelToken);
  }

  prepareParams(params) {
    const prepParams = { ...this.state.filterParams, ...params };
    // in API we will consume accounts as string ( 'accountID1, accountID2' )
    if (prepParams.account__id.length > 0) {
      prepParams.account_ids = prepParams.account__id.map(l => (l.value)).join(', ');
      delete prepParams.account__id;
    }
    // in API we will consume managers as string ( 'managerID1, managerID2' )
    if (prepParams.campaign_manager__id.length > 0) {
      prepParams.campaign_manager_ids = prepParams.campaign_manager__id.map(l => (l.value))
        .join(', ');
      delete prepParams.campaign_manager__id;
    }
    // in API we will consume statuses as string ( 'not_interested,interested' )
    if (prepParams.statuses.length > 0) {
      prepParams.statuses = prepParams.statuses.map(values => (values.value));
    }
    // in API we will consume campaigns as string ( 'campaignID1, campaignID2' )
    if (prepParams.campaign__id.length > 0) {
      prepParams.campaign_ids = prepParams.campaign__id.map(l => (l.value)).join(', ');
      delete prepParams.campaign__id;
    }
    // in API we will consume opportunities as string ( 'opportunityID1, opportunityID2' )
    if (prepParams.opportunity__id.length > 0) {
      if (prepParams.opportunity__id.length === 1 &&
        (prepParams.opportunity__id[0].value === 'all' ||
          prepParams.opportunity__id[0].value === 'unknown')) {
        delete prepParams.opportunity__id;
      } else {
        prepParams.opportunity_ids = prepParams.opportunity__id.map(l => (l.value)).join(', ');
        delete prepParams.opportunity__id;
        delete prepParams.opportunity__isnull;
      }
    }
    // round date to dates with UTC
    if (prepParams.startDate) {
      prepParams.date_after = prepParams.startDate.startOf('day').toISOString();
      delete prepParams.startDate;
    }
    if (prepParams.endDate) {
      prepParams.date_before = prepParams.endDate.endOf('day').toISOString();
      delete prepParams.endDate;
    }
    if (prepParams.labels) {
      prepParams.labels = prepParams.labels.map(o => o.value);
    }
    return prepParams;
  }

  showNotification = (message, errorType = 'success') => {
    this.notification.notice({
      content: <BasicNotification
        color={errorType}
        message={message}
      />,
      duration: 6,
      closable: false,
      style: NOTIFICATION_NODE_STYLE,
      className: 'alert--colored right-up',
    });
  }

  toggleBulkModal(isOpen) {
    this.setState({ isBulkModalOpen: isOpen });
  }

  isIndeterminate() {
    const numberOfSelectedOutreachesOnPage = this.getSelectedOutreachesOnPage().length;
    return numberOfSelectedOutreachesOnPage > 0 && numberOfSelectedOutreachesOnPage < PAGE_SIZE_LARGE;
  }

  isAllChecked() {
    const numberOfSelectedOutreachesOnPage = this.getSelectedOutreachesOnPage().length;
    return numberOfSelectedOutreachesOnPage === PAGE_SIZE_LARGE;
  }

  isSelected = id => this.state.selectedIds.has(id);

  handleSelectAll = () => {
    const numberOfSelectedOutreachesOnPage = this.getSelectedOutreachesOnPage().length;
    const selectedIdsCopy = new Set(this.state.selectedIds);
    // if we have at least one selected outreach then we are isIndeterminate
    if (numberOfSelectedOutreachesOnPage > 0) {
      // remove all on current page
      for (let i = 0; i < this.props.messages.results.length; i += 1) {
        const outreach = this.props.messages.results[i];
        if (selectedIdsCopy.has(outreach.id)) selectedIdsCopy.delete(outreach.id);
      }
    } else {
      // add all on the current page
      this.props.messages.results.forEach(o => selectedIdsCopy.add(o.id));
    }
    this.setState({ selectedIds: selectedIdsCopy });
  };

  handleSelectClick = (evt, id) => {
    const { lastFocusedRow, selectedIds } = this.state;
    const { messages } = this.props;
    const selectedIdsCopy = new Set(selectedIds);
    const isFocusedContactOnPage = messages.results.some(selOutreach => selOutreach.id === lastFocusedRow);
    if (evt.shiftKey && isFocusedContactOnPage && id !== lastFocusedRow) {
      // handle click + shift (select rows between last focused row and clicked row)
      // if clickedIndex is selected -> remove all newSelectedIds
      // if clickedIndex is not selected -> add all newSelectedIds
      const lastFocusedIndex = messages.results.findIndex(obj => obj.id === lastFocusedRow);
      const clickedIndex = messages.results.findIndex(obj => obj.id === id);
      if (lastFocusedIndex >= 0 && clickedIndex >= 0) {
        const newSelectedObjs = clickedIndex > lastFocusedIndex ?
          messages.results.slice(lastFocusedIndex, clickedIndex + 1) :
          messages.results.slice(clickedIndex, lastFocusedIndex + 1);
        const newSelectedIds = new Set(newSelectedObjs.map(selOutreach => selOutreach.id));
        const isClickedIndexSelected = selectedIds.has(id);
        if (isClickedIndexSelected) {
          newSelectedIds.forEach(selOutreachId => selectedIdsCopy.delete(selOutreachId));
        } else {
          newSelectedIds.forEach(selOutreachId => selectedIdsCopy.add(selOutreachId));
        }
      }
    } else if (selectedIds.has(id)) {
      selectedIdsCopy.delete(id);
    } else {
      selectedIdsCopy.add(id);
    }
    this.setState({ selectedIds: selectedIdsCopy, lastFocusedRow: id });
  };

  untoggleSelectedOutreachesOnPage() {
    const selectedOutreachesOnPage = this.getSelectedOutreachesOnPage();
    const selectedIdsCopy = new Set(this.state.selectedIds);
    for (let i = 0; i < selectedOutreachesOnPage.length; i += 1) {
      const selOutreach = selectedOutreachesOnPage[i];
      if (selectedIdsCopy.has(selOutreach.id)) selectedIdsCopy.delete(selOutreach.id);
    }
    this.setState({ selectedIds: selectedIdsCopy });
  }

  handleBulkModalSuccess = async () => {
    const selectedOutreachesOnPage = this.getSelectedOutreachesOnPage();
    await this.props.destroyBulkMessage(selectedOutreachesOnPage);
    this.toggleBulkModal(false);
    // untoggle selected outreaches from being selected
    this.untoggleSelectedOutreachesOnPage();
    // show notifications
    this.showNotification(`${selectedOutreachesOnPage.length} messages were successfully deleted`, 'success');
    this.fetchList();
  }

  downloadCSV = async () => {
    const preparedParams = this.prepareParams(this.state.filterParams);
    analyticsTrack(DOWNLOAD_INBOX_CSV, preparedParams, true);

    redirectApp(apiInboxExportCSV(), preparedParams);
  }

  handleDelete = (id) => {
    this.setState({ selectedOutreach: id, openDeleteModel: true });
  };

  toggleAreYouSure = async (answer) => {
    if (answer) {
      await this.props.destroyMessage(this.state.selectedOutreach);
      this.fetchList({}, true);
      this.setState({ openDeleteModel: false });
    } else {
      this.setState({ openDeleteModel: answer });
    }
  };

  render() {
    const {
      messages,
      messageId,
      campaignId,
      classes,
      filterStartDate,
      includeUnknownCampaignOption,
    } = this.props;
    const {
      filterParams, isBulkModalOpen,
      isSliding, selectedIds,
    } = this.state;
    const isLoading = messages.loading;
    const isOutreaches = messages.results.length > 0;

    const accountId = get(filterParams, 'account__id.0.value', null);
    const isCampaignDetailInbox = window.location.pathname.includes('campaigns');
    const canSeeOpportunity = this.context.can('see', 'Opportunity');
    return (
      <React.Fragment>
        {this.state.openOpportunityViewID && (
          <OpportunityView
            toggle={this.toggleOpportunityView}
            opportunityId={this.state.openOpportunityViewID}
          />
        )}
        <ConfirmationModal
          open={isBulkModalOpen}
          onBackdropClick={() => this.toggleBulkModal(false)}
          title="Delete"
          confirmLabel="Delete"
          onConfirmClick={this.handleBulkModalSuccess}
          onCancelClick={() => this.toggleBulkModal(false)}
        >
          {selectedIds.size} messages will be&nbsp;
          <span className="volt-modal--red">DELETED</span>
        </ConfirmationModal>
        <ConfirmationModal
          open={this.state.openDeleteModel}
          onBackdropClick={() => this.toggleAreYouSure(false)}
          title="Delete item"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirmClick={() => this.toggleAreYouSure(true)}
          onCancelClick={() => this.toggleAreYouSure(false)}
        >
          Are you sure you want to delete this message ?
        </ConfirmationModal>
        <div className={classNames('outreach-list', { 'mt-3': !campaignId })} >
          <div className={classes.optionsWrapper}>
            <div className={classes.filterWrapper}>
              <DynamicFilter
                onResetSelectedFilter={this.onResetSelectedFilter}
                onResetFilters={this.onResetFilters}
                options={[
                  {
                    key: FilterTypes.manager,
                    menuLabel: 'Campaign Manager',
                    onDeleteFilter: this.onDeleteFilter,
                    isDefault: this.checkURLHaveFilterByAlias(FilterTypes.manager),
                    component: <CampaignManagersFilter
                      value={filterParams.campaign_manager__id}
                      onChange={this.handleChangeManagers}
                    />,
                  },
                  {
                    key: FilterTypes.category,
                    menuLabel: 'Inbox categories',
                    onDeleteFilter: this.onDeleteFilter,
                    isDefault: this.checkURLHaveFilterByAlias(FilterTypes.category),
                    component: <Select
                      isMulti
                      placeholder="Categories"
                      onChange={this.handleChangeCategories}
                      options={REPLY_STATUS_OPTIONS}
                      value={filterParams.statuses}
                      isCategorySelect
                    />,
                  },
                  {
                    key: FilterTypes.date,
                    menuLabel: 'Date',
                    onDeleteFilter: this.onDeleteFilter,
                    isDefault: (!messageId && filterStartDate && !filterParams.redirect && !this.isFilterParamsInQuery()) || this.checkURLHaveFilterByAlias(FilterTypes.date),
                    component: <DateRangePicker
                      initialStartDate={filterParams.startDate}
                      initialEndDate={filterParams.endDate}
                      onChange={this.handleDateChange}
                      minDate={null}
                      maxDate={moment()}
                    />,
                  },
                  {
                    key: FilterTypes.status,
                    menuLabel: 'Status',
                    onDeleteFilter: this.onDeleteFilter,
                    isDefault: (!campaignId && !messageId && !filterParams.redirect && !this.isFilterParamsInQuery()) || this.checkURLHaveFilterByAlias(FilterTypes.status),
                    component: <Select
                      isMulti
                      placeholder="Status"
                      onChange={this.handleChangeStatus}
                      options={MESSAGE_LABEL_OPTIONS}
                      value={filterParams.labels}
                    />,
                  },
                  ...(
                    canSeeOpportunity ? [
                      {
                        key: FilterTypes.opportunities,
                        menuLabel: 'Opportunities',
                        onDeleteFilter: this.onDeleteFilter,
                        isDefault: (!this.isFilterParamsInQuery() || this.checkURLHaveFilterByAlias(FilterTypes.opportunities)) && !campaignId,
                        component: <OpportunityFilter
                          value={filterParams.opportunity__id}
                          onChange={this.handleChangeOpportunities}
                          options={[{ label: 'All', value: 'all' }]}
                        />,
                      },
                    ] : []),
                  ...(!campaignId ? [
                    {
                      key: FilterTypes.account,
                      menuLabel: 'Accounts',
                      onDeleteFilter: this.onDeleteFilter,
                      isDefault: this.checkURLHaveFilterByAlias(FilterTypes.account),
                      component: <AccountsFilter
                        value={filterParams.account__id}
                        onChange={this.handleChangeAccounts}
                      />,
                    },
                    {
                      key: FilterTypes.campaign,
                      menuLabel: 'Campaigns',
                      onDeleteFilter: this.onDeleteFilter,
                      isDefault: this.checkURLHaveFilterByAlias(FilterTypes.campaign),
                      component: <CampaignsFilter
                        value={filterParams.campaign__id}
                        onChange={this.handleChangeCampaigns}
                        accountId={accountId}
                        includeUnknownOption={includeUnknownCampaignOption}
                      />,
                    },
                  ] : []),
                ]}
              />
            </div>
            <div>
              <Button
                color="primary"
                variant="contained"
                type="text/csv"
                disabled={!isOutreaches || isLoading || this.state.disableCSVDownload}
                onClick={this.downloadCSV}
                startIcon={<DownloadIcon />}
              >
                Download Detailed Leads
              </Button>
            </div>
          </div>

          <div className={classes.slideContainer}>
            {(isLoading || this.state.bulkOutreachLoading) &&
              <div className={`loader ${classes.loaderList}`}><LoadingIcon /></div>}

            <div
              className={
                `material-table__wrap
                  ${(!isOutreaches || isLoading) ? classes.noResultsTable : ''}
                  ${classes.outreachTable}
                `}
            >
              {isOutreaches && !isLoading &&

              <React.Fragment>
                <Can I="select" a="Message">
                  <div className="header">
                    <div className={classes.checkBoxAll}>
                      <Checkbox
                        indeterminate={this.isIndeterminate()}
                        checked={this.isAllChecked()}
                        onChange={this.handleSelectAll}
                      />
                    </div>
                    {selectedIds.size > 0 &&
                      <>
                        <div>
                          <span className="border-left mr-3" />
                          <MessageActionIcons
                            handleChangeCategory={this.handleChangeMoveTo}
                            handleChangeAction={this.handleChangeActions}
                          />
                        </div>
                        <div className="selected-number ml-4 flex-grow-1">
                          <div className="material-table__toolbar-selected float-right">
                            {selectedIds.size}&nbsp;
                            <span>selected</span>
                          </div>
                        </div>
                      </>
                    }
                  </div>
                </Can>
                <div
                  className={classNames(classes.outreachesContainer, {
                    [classes.campaignDetailInbox]: isCampaignDetailInbox,
                  })}
                >
                  {
                    messages.results.map((outreach) => {
                      const lastMsg = getLastMsg(outreach);
                      return (<OutreachRow
                        key={lastMsg.id + lastMsg.reply_status}
                        outreach={outreach}
                        lastMsg={lastMsg}
                        isSliding={isSliding}
                        detailOpen={this.props.message.isOpen}
                        handleSelectClick={this.handleSelectClick}
                        isSelected={this.isSelected(outreach.id)}
                        toggleDetail={this.toggleDetail}
                        handleArchive={this.fetchList}
                        isDetailView={campaignId}
                        handleDelete={this.handleDelete}
                        deleteOutreachId={this.state.selectedOutreach}
                        isDeleteOpen={this.state.openDeleteModel}
                        onClickOpportunityTag={() => this.toggleOpportunityView(outreach.opportunity.id)}
                      />);
                    })
                  }
                  <div className="show-border-to-bottom" />
                  <Pagination
                    className="mt-4 mb-4 ml-3 float-right"
                    pageSize={PAGE_SIZE_LARGE}
                    items={messages}
                    onChangePage={this.handleChangePage}
                    params={filterParams}
                  />
                </div>
              </React.Fragment>
              }
              {(!isOutreaches && !isLoading) &&
                <div className={classes.noRepliesStateContainer}>
                  <BinocularIcon className={classes.noRepliesIcon} viewBox="0 0 92 92" />
                  <h3 className={classes.noRepliesText}>No mails matched your filter.</h3>
                </div>
              }
            </div>

          </div>

        </div>
      </React.Fragment>
    );
  }
}

OutreachList.contextType = AbilityContext;

export default compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(
    state => ({
      user: state.user,
      users: state.users,
      accounts: state.accounts,
      campaign: state.campaign,
      campaigns: state.campaigns,
      messages: state.messages,
      message: state.message,
    }),
    {
      getMessages,
      setMessage,
      getOutreach,
      getMessage,
      updateOutreachThreadStatusBulk,
      setAutoCategorizedDateMessage,
      setOutreach,
      patchMessage,
      setMessageAsReadBulk,
      setMessageArchivedBulk,
      destroyBulkMessage,
      destroyMessage,
      toggleInboxDetail,
      resetMessage,
      resetMessages,
      getOpportunities,
    },
  ),
)(OutreachList);
